import { BaseApiService } from './BaseApiService';
import { DateRange } from './DashboardService';

export interface WebstaurantCategorySpend {
  category: string;
  amount: number;
  itemCount: number;
}

export interface WebstaurantWeekSpend {
  weekEnding: string; // ISO date of the week's end (Sunday)
  spend: number;
  itemCount: number;
}

export interface WebstaurantItemSpend {
  itemNumber: string;
  description: string;
  category: string;
  quantity: number;
  amount: number;
  lineCount: number;
}

export interface WebstaurantWeekDetail {
  weekEnding: string;
  spend: number;
  itemCount: number;
  items: WebstaurantItemSpend[];
}

export interface WebstaurantStoreDetail {
  location: string;
  weeks: WebstaurantWeekDetail[];
}

export interface WebstaurantLocation {
  location: string;
  locationType: 'store' | 'corporate' | 'new_store';
  tier: string | null;
  weeklySales: number | null;
  weeklySalesToast: number | null;
  weeklySalesEzCater: number | null;
  spendTotal: number;
  spendWeeklyAvg: number;
  percentOfSales: number | null;
  thisWeekSpend: number;
  thisWeekItems: number;
  thisWeekSales: number | null;
  thisWeekPercentOfSales: number | null;
  trendPct: number | null;
  itemCount: number;
  categoryBreakdown: WebstaurantCategorySpend[];
  weeklyBreakdown: WebstaurantWeekSpend[];
  itemBreakdown: WebstaurantItemSpend[];
  thisWeekItemBreakdown: WebstaurantItemSpend[];
}

export interface WebstaurantData {
  startDate: string;
  endDate: string;
  weeksInRange: number;
  totalSpend: number;
  storeSpend: number;
  totalWeeklySales: number;
  availableCategories: string[];
  selectedCategories: string[];
  stores: WebstaurantLocation[];
  corporate: WebstaurantLocation[];
  newStores: WebstaurantLocation[];
}

export class WebstaurantService extends BaseApiService {
  private static instance: WebstaurantService;

  private constructor() {
    super();
  }

  public static getInstance(): WebstaurantService {
    if (!WebstaurantService.instance) {
      WebstaurantService.instance = new WebstaurantService();
    }
    return WebstaurantService.instance;
  }

  async getWebstaurantData(
    startDate: string,
    endDate: string,
    categories?: string[]
  ): Promise<WebstaurantData> {
    let url = `/api/webstaurant/data?startDate=${startDate}&endDate=${endDate}`;
    if (categories && categories.length > 0) {
      url += `&categories=${categories.map((c) => encodeURIComponent(c)).join(',')}`;
    }
    return this.get<WebstaurantData>(url, undefined, true);
  }

  async getAvailableCategories(): Promise<string[]> {
    return this.get<string[]>('/api/webstaurant/categories', undefined, true);
  }

  // Full weekly history for one store (all weeks + that week's products), loaded on expand.
  async getStoreDetail(location: string, categories?: string[]): Promise<WebstaurantStoreDetail> {
    let url = `/api/webstaurant/store-detail?location=${encodeURIComponent(location)}`;
    if (categories && categories.length > 0) {
      url += `&categories=${categories.map((c) => encodeURIComponent(c)).join(',')}`;
    }
    return this.get<WebstaurantStoreDetail>(url, undefined, false);
  }

  // Only the 8-week windows that actually have Webstaurant data, most recent first.
  async getWeeksWithData(): Promise<DateRange[]> {
    return this.get<DateRange[]>('/api/webstaurant/weeks', undefined, true);
  }
}

export default WebstaurantService.getInstance();
